import React, { useEffect, useState } from "react";
import { Row, Col, Card, Table, Badge, Button, Spinner } from "react-bootstrap";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Calendar, User, Phone, Sparkles, ChevronRight } from "lucide-react";
import Pagination from "../components/Pagination";
import { getPartnerHost, getPartnerDetail } from "../../services/UserServices/UserService";

export const getImageUrl = (path) => {
  if (!path) return "";
  if (
    path.startsWith("blob:") ||
    path.startsWith("http://") ||
    path.startsWith("https://") ||
    path.startsWith("data:")
  ) {
    return path;
  }
  const cleanBase = "https://managemyshaadib.s3.ap-south-1.amazonaws.com/".replace(/\/+$/, "");
  const cleanPath = path.startsWith("/") ? path : `/${path}`;
  return `${cleanBase}${cleanPath}`;
};


const PartnerEvents = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [partner, setPartner] = useState(null);
  const [hosts, setHosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalHosts, setTotalHosts] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    const fetchPartnerInfo = async () => {
      if (!id) return;
      try {
        const res = await getPartnerDetail(id);
        setPartner(res?.data?.data || res?.data);
      } catch (err) {
        console.error("Error fetching partner details:", err);
      }
    };
    fetchPartnerInfo();
  }, [id]);

  useEffect(() => {
    const fetchHosts = async () => {
      if (!id) return;
      try {
        setLoading(true);
        const res = await getPartnerHost(id, currentPage + 1, itemsPerPage);
        const list = res.data?.hosts || res.data?.data?.hosts || res.data?.data || res.data || [];
        const total =
          res.data?.count ??
          res.data?.total ??
          res.data?.totalCount ??
          res.data?.totalHosts ??
          res.data?.data?.count ??
          res.data?.data?.total ??
          res.data?.data?.totalCount ??
          (Array.isArray(list) ? list.length : 0);
        const pages =
          res.data?.totalPages ??
          res.data?.pages ??
          res.data?.data?.totalPages ??
          (Math.ceil(total / itemsPerPage) || 0);
        setHosts(Array.isArray(list) ? list : []);
        setTotalHosts(total);
        setTotalPages(pages);
      } catch (err) {
        console.error("Error fetching partner hosts:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchHosts();
  }, [id, currentPage]);

  const partnerName = partner?.hostName || partner?.partnerData?.companyName || "Partner";

  return (
    <div className="container-fluid p-0">
      <style>{`
        .genz-card-box {
          background: #ffffff !important;
          border-radius: 1.5rem !important;
          overflow: hidden !important;
          border: 1.5px solid #F8DBA0 !important;
          box-shadow: 0 10px 30px rgba(166, 0, 0, 0.04) !important;
        }
        .genz-btn-action {
          background: linear-gradient(135deg, #A60000 0%, #800001 100%) !important;
          color: #A60000 !important;
          font-weight: 800 !important;
          border: 1.5px solid #A60000 !important;
          box-shadow: 0 6px 20px rgba(166, 0, 0, 0.25) !important;
          border-radius: 50rem !important;
          padding: 0.5rem 1.2rem !important;
        }
        .genz-badge-gold {
          background: linear-gradient(135deg, #A60000 0%, #800001 100%) !important;
          color: #A60000 !important;
          font-weight: 800 !important;
          font-size: 0.8rem !important;
          padding: 0.4rem 0.9rem !important;
          border-radius: 50rem !important;
        }
      `}</style>

      {/* Header Bar */}
      <div className="d-flex align-items-center justify-content-between flex-wrap gap-3 mb-4">
        <div className="d-flex align-items-center gap-3">
          <Button
            variant="white"
            size="sm"
            className="shadow-sm border rounded-circle p-2 d-flex align-items-center justify-content-center"
            style={{ width: "44px", height: "44px", borderColor: "#F8DBA0", backgroundColor: "#ffffff" }}
            onClick={() => navigate(-1)}
          >
            <ArrowLeft size={20} style={{ color: "#800001" }} />
          </Button>
          <div>
            <div className="d-flex align-items-center gap-2">
              <h3 className="fw-extrabold mb-0" style={{ color: "#800001", letterSpacing: "-0.03em" }}>
                {partnerName}'s Hosts ({totalHosts})
              </h3>
              <Sparkles size={20} style={{ color: "#A60000" }} />
            </div>
         
          </div>
        </div>

       
      </div>

      <Card className="qx-table-card">
        <Card.Body>
          {loading ? (
            <div className="d-flex justify-content-center align-items-center py-5">
              <Spinner animation="border" variant="danger" />
            </div>
          ) : hosts && hosts.length > 0 ? (
            <>
              <Table responsive className="mb-0">
                <thead style={{ color: "#A60000", backgroundColor: "#ffffff" }}>
                  <tr>
                    <th><strong>S.NO</strong></th>
                    <th><strong>Couple & Host Name</strong></th>
                    <th><strong>Phone Number</strong></th>
                    <th><strong>Host Side</strong></th>
                    <th><strong>Plan Name</strong></th>
                    <th><strong>Created Date</strong></th>
                    <th><strong>Guests</strong></th>
                    <th><strong>Invites</strong></th>
                    <th><strong>RSVPs</strong></th>
                      <th><strong>View</strong></th>
                  </tr>
                </thead>
                <tbody>
                  {hosts.map((host, idx) => {
                    const coupleImg = host.couplePhoto ? getImageUrl(host.couplePhoto) : null;
                    const coupleNames =
                      host.groomName && host.brideName
                        ? `${host.groomName} & ${host.brideName}`
                        : host.hostName || "N/A";
                    const formattedDate = host.createdAt
                      ? new Date(host.createdAt).toLocaleDateString("en-IN", {
                          day: "2-digit",
                          month: "long",
                          year: "numeric",
                        })
                      : "-";

                    return (
                      <tr key={host._id || host.id || idx} style={{ color: "#A60000" }}>
                        <td>{currentPage * itemsPerPage + idx + 1}</td>
                        <td>
                          <div className="d-flex align-items-center gap-2">
                            {coupleImg ? (
                              <img
                                src={coupleImg}
                                alt={coupleNames}
                                className="rounded-circle object-fit-cover"
                                style={{ width: 34, height: 34, border: "1.5px solid #A60000" }}
                              />
                            ) : (
                              <div
                                className="d-flex align-items-center justify-content-center rounded-circle fw-bold"
                                style={{ width: 34, height: 34, fontSize: "0.8rem", backgroundColor: "rgba(166, 0, 0,0.1)", color: "#A60000" }}
                              >
                                {host.hostName ? host.hostName.charAt(0).toUpperCase() : "W"}
                              </div>
                            )}
                            <div>
                              <div className="fw-bold mb-0" style={{ fontSize: "0.85rem" }}>
                                {coupleNames}
                              </div>
                              <span  onClick={()=>navigate(`/user-details/${host?._id}`)} className="small text-blue cursor-pointer" style={{ fontSize: "0.75rem" }}>
                                Host: {host.hostName || "-"}
                              </span>
                            </div>
                          </div>
                        </td>
                        <td>{host.countryCode || "+91"} {host.phoneNumber || "-"}</td>
                        <td>
                          <Badge bg={host.hostSide === "bride" ? "danger" : "primary"}>
                            {host.hostSide || "-"}
                          </Badge>
                        </td>
                        <td>
                          <Badge style={{color:"white"}} bg="warning" >
                            {host.plan?.name || "-"}
                          </Badge>
                        </td>
                        <td>{formattedDate}</td>
                        <td>{host.totalGuests ?? 0}</td>
                        <td>{host.totalInvites ?? 0}</td>
                        <td>{host.totalRsvps ?? 0}</td>
                        <td>
                            <button
                          type="button"
                          className="qx-action-view"
                          title="View event"
                          aria-label="View event"
                          onClick={() =>
                            navigate(`/user-details/${host?._id}`)
                          }
                        >
                          <ChevronRight size={18} strokeWidth={2.5} />
                        </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>

              <Pagination
                pageCount={totalPages}
                pageValue={currentPage}
                setPage={setCurrentPage}
                total={totalHosts}
                pageSize={itemsPerPage}
                itemLabel="weddings"
              />
            </>
          ) : (
            <div className="p-5 text-center my-4 text-muted">
              <Calendar size={42} className="mb-2 text-danger opacity-50" />
              <h6 className="fw-bold mb-1">No Weddings Created</h6>
              <p className="small mb-0">This partner has not created any wedding events yet.</p>
            </div>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default PartnerEvents;
